import { makeStyles } from '@lifeomic/chroma-react/styles';
import { alpha } from '@mui/material/styles';

export const useMemberDetailStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    maxWidth: '100%',
    minWidth: 0,
  },
  headerPatient: {
    '&&': {
      flex: '0 0 auto',
      maxWidth: '100%',
      overflow: 'visible',
    },
  },
  headerIconGroup: {
    '&&': {
      flex: '0 0 auto',
      justifyContent: 'center',
      margin: 0,
      order: 0,
      width: 'min(100%, max-content)',
      maxWidth: '100%',
      overflow: 'visible',
    },
  },
  headerActionsSlot: {
    '&&': {
      flex: '0 1 auto',
      justifyContent: 'flex-end',
      marginLeft: 'auto',
      maxWidth: '100%',
      minWidth: 'min(100%, max-content)',
      overflow: 'visible',
    },
  },
  pageLayout: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  pageRoot: {
    height: '100%',
    padding: 0,
  },
  // Keeps this page inside the app shell so the extractions list scrolls,
  // instead of the list growing and the shell scrolling behind a hidden bar.
  extractionsPageRoot: {
    '&&': {
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    },
  },
  layoutContainer: {
    flex: 1,
    minHeight: 0,
    marginBottom: 0,
  },
  headerBackgroundPadding: {
    '&&': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      minHeight: 0,
      alignItems: 'center',
      flexWrap: 'wrap',
      rowGap: theme.spacing(0.5),
      columnGap: theme.spacing(2),
    },
  },
  backgroundCover: {
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0,
  },
  headerName: {
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  headerContentContainer: {
    minWidth: 0,
    width: '100%',
  },
  headerDob: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  headerMetaItem: {
    ...theme.typography.caption,
  },
  elationLink: {
    whiteSpace: 'nowrap',
  },
  elationLinkContent: {
    ...theme.typography.caption,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  contentArea: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  detailBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },
  cohortMismatchBanner: {
    flexShrink: 0,
    width: '100%',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  menuActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '100%',
  },
  sectionTabList: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.25),
    border: 'none',
    minHeight: 0,
    maxWidth: '100%',
    '&&': {
      width: '100%',
      maxWidth: '100%',
      overflow: 'visible',
    },
    '& > *': {
      flexShrink: 0,
    },
  },
  sectionTabTooltipWrapper: {
    display: 'inline-flex',
    flexShrink: 0,
  },
  sectionTab: {
    position: 'relative',
    justifyContent: 'center',
    flexShrink: 0,
    '&&': {
      overflow: 'visible',
      flexShrink: 0,
      minWidth: 32,
      width: 32,
      minHeight: 32,
      height: 32,
      padding: 0,
    },
    '& svg': {
      width: 20,
      height: 20,
    },
    '& $sectionTabBadge': {
      position: 'absolute',
      top: 0,
      right: -2,
      height: 16,
      maxHeight: 16,
      minWidth: 16,
      padding: '0 4px',
      fontSize: 10,
      lineHeight: '16px',
      letterSpacing: 0,
      textIndent: 0,
      textTransform: 'none',
      pointerEvents: 'none',
    },
  },
  sectionTabBadge: {},
  sectionTabIcon: {
    width: 20,
    height: 20,
  },
  menuButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: 0,
    whiteSpace: 'nowrap',
    position: 'relative',
    left: theme.spacing(-4),
  },
  menuLabelText: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  menuItemSelected: {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  menuItemLink: {
    display: 'block',
    textDecoration: 'none',
    color: 'inherit',
    width: '100%',
  },
  menuItemWrapper: {
    width: '100%',
    pointerEvents: 'none',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    maxWidth: '100%',
  },
  contentLayout: {
    display: 'flex',
    gap: theme.spacing(1),
    flex: 1,
    minHeight: 0,
    alignItems: 'stretch',
    position: 'relative',
  },
  mainContent: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
  landingContainer: {
    height: '100%',
    overflow: 'auto',
    padding: theme.spacing(2, 3, 3),
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  landingSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  landingSectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  landingSectionSubtitle: {
    color: theme.palette.text.secondary,
  },
  landingQuickTilesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: theme.spacing(2),
    width: '100%',
  },
  landingCompactTilesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: theme.spacing(1.5),
    width: '100%',
  },
  landingTileBase: {
    appearance: 'none',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    color: theme.palette.text.primary,
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    width: '100%',
    '&:hover': {
      boxShadow: theme.boxShadows.focusVisible,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  landingQuickTile: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    padding: theme.spacing(2),
  },
  landingQuickTileIconBadge: {
    alignItems: 'center',
    backgroundColor: alpha(
      theme.palette.mode === 'dark'
        ? theme.palette.blue[200]
        : theme.palette.primary.main,
      theme.palette.mode === 'dark' ? 0.15 : 0.075,
    ),
    borderRadius: '50%',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.blue[200]
        : theme.palette.primary.main,
    display: 'flex',
    flexShrink: 0,
    height: 72,
    justifyContent: 'center',
    width: 72,
  },
  landingQuickTileIcon: {
    height: 36,
    width: 36,
  },
  landingQuickTileContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    textAlign: 'center',
    width: '100%',
  },
  landingCompactTile: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
  },
  landingCompactTileIconBadge: {
    alignItems: 'center',
    backgroundColor: alpha(
      theme.palette.mode === 'dark'
        ? theme.palette.blue[200]
        : theme.palette.primary.main,
      theme.palette.mode === 'dark' ? 0.15 : 0.075,
    ),
    borderRadius: '50%',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.blue[200]
        : theme.palette.primary.main,
    display: 'flex',
    flexShrink: 0,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  landingCompactTileIcon: {
    height: 22,
    width: 22,
  },
  landingCompactTileContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  landingTileChevron: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    height: 20,
    width: 20,
  },
  landingTileTitle: {
    lineHeight: 1.25,
    color: theme.palette.text.primary,
  },
  landingTileDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  chatWrapper: {
    display: 'flex',
    alignSelf: 'stretch',
    height: '100%',
    marginLeft: 'auto',
    position: 'relative',
    flex: '0 0 auto',
  },
  chatWrapperOverlay: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 100,
    marginLeft: 0,
    boxShadow: theme.boxShadows.elevatedContent,
  },
  chatWrapperFullscreen: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    marginLeft: 0,
    height: '100vh',
    width: '100vw',
    backgroundColor: theme.palette.background.paper,
  },
  chatResizable: {
    height: '100%',
    display: 'flex',
  },
  chatResizableFullscreen: {
    width: '100%',
    height: '100%',
  },
  chatSidebar: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    height: '100%',
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  chatSidebarFullscreen: {
    borderLeft: 'none',
  },
  chatHeader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  chatContent: {
    flex: 1,
    minHeight: 0,
  },
  resizeHandle: {
    backgroundColor: theme.palette.primary.main,
    cursor: 'ew-resize',
    height: '100%',
    opacity: 0,
    position: 'absolute',
    left: 0,
    top: 0,
    width: 3,
    zIndex: 1000,
    '&:hover': {
      opacity: 1,
      transition: 'opacity 0.2s ease-out',
    },
  },
}));
